import { v4 as uuid } from "uuid";

import { issue__EventType, Participant } from "@medbillai/graphql-types";

import type {
  ChatEvent,
  DocumentUploadedEvent,
  UserSelectionRequestedEvent,
} from "./types";

/**
 * Create a user chat event for preview cases with all required fields
 */
export function createUserChatEvent(message: string): ChatEvent {
  return {
    __typename: "issue__ChatEvent_Type" as const,
    id: uuid().toString(),
    eventType: issue__EventType.CHAT,
    participant: Participant.USER,
    chatBody: message,
    sentAt: new Date().toISOString(),
    userVisible: true,
    eventFeedbacks: [],
    sentByProfile: {
      __typename: "user__Profile_Type" as const,
      name: "You",
    },
  };
}

/**
 * Create a system message event for preview cases with all required fields.
 * If options are provided, creates a UserSelectionRequestedEvent.
 * Otherwise, creates a regular chat event.
 */
export function createSystemMessageEvent(
  message: string,
  options?: string[],
): UserSelectionRequestedEvent | ChatEvent {
  if (options && options.length > 0) {
    return {
      __typename: "issue__UserSelectionRequestedEvent_Type" as const,
      id: uuid().toString(),
      eventType: issue__EventType.USER_SELECTION_REQUESTED,
      participant: Participant.SERVICE,
      message,
      sentAt: new Date().toISOString(),
      stringOptions: options,
      userVisible: true,
      eventFeedbacks: [],
      sentByProfile: {
        __typename: "user__Profile_Type" as const,
        name: null,
      },
    };
  }

  return {
    __typename: "issue__ChatEvent_Type" as const,
    id: uuid().toString(),
    eventType: issue__EventType.CHAT,
    participant: Participant.SERVICE,
    chatBody: message,
    sentAt: new Date().toISOString(),
    userVisible: true,
    eventFeedbacks: [],
    sentByProfile: {
      __typename: "user__Profile_Type" as const,
      name: null,
    },
  };
}

/**
 * Create a document uploaded event for preview cases with all required fields
 */
export function createDocumentUploadEvent(
  { uploading }: { uploading?: boolean } = { uploading: false },
): DocumentUploadedEvent {
  return {
    __typename: "issue__DocumentUploadedEvent_Type" as const,
    id: uuid().toString(),
    eventType: issue__EventType.DOCUMENT_UPLOADED,
    participant: Participant.USER,
    sentAt: new Date().toISOString(),
    userVisible: true,
    includeTime: false,
    eventFeedbacks: [],
    sentByProfile: {
      __typename: "user__Profile_Type" as const,
      name: null,
    },
    uploading,
    document: {
      __typename: "document__Document_Type" as const,
      id: uuid().toString(),
      name: "document",
      createdAt: new Date().toISOString(),
      files: [],
      dataType: null,
      latestIngestion: null,
    },
  };
}
